import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Star, ThumbsUp, ThumbsDown } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

export const meta = {
  id: 'ticket_ratings',
  category: 'tickets',
  titleKey: 'dashboard.widgets.ticketRatings',
  descriptionKey: 'dashboard.widgets.ticketRatingsDesc',
  defaultSize: 'md',
  minRole: 'manager',
  icon: Star,
};

// Uses the existing /api/tickets/ratings endpoint (manager+ only).
// Shows: rolling avg rating across the team, count of positives/negatives,
// and the last few comments so issues are visible without leaving the page.
export default function TicketRatingsWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const locale = user?.language === 'en' ? enUS : es;

  // Window: last 30 days. The endpoint accepts date_from/date_to but treats
  // them as inclusive — we pass a 30d window for a stable rolling average.
  const dateFrom = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const { data } = useQuery({
    queryKey: ['tickets-ratings-dashboard', dateFrom],
    queryFn: () => api.get('/tickets/ratings', { params: { date_from: dateFrom, limit: 10 } }).then(r => r.data),
    refetchInterval: 10 * 60 * 1000,
  });

  const summary = data?.summary || [];
  const recent = (data?.ratings || []).slice(0, 4);

  // Roll up the per-agent summary into a team-level average + counts.
  const totalRatings = summary.reduce((s, r) => s + (parseInt(r.total_ratings) || 0), 0);
  const weightedSum = summary.reduce((s, r) => s + ((parseFloat(r.avg_rating) || 0) * (parseInt(r.total_ratings) || 0)), 0);
  const teamAvg = totalRatings > 0 ? (weightedSum / totalRatings) : null;
  const positives = summary.reduce((s, r) => s + (parseInt(r.positive) || 0), 0);
  const negatives = summary.reduce((s, r) => s + (parseInt(r.negative) || 0), 0);

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', height: '100%' }}>
      <div className="card-header" style={{ padding: '14px 20px', borderBottom: '1px solid var(--c-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Star size={16} />
          {t('dashboard.widgets.ticketRatings')}
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/agent-ratings')}>
          {t('dashboard.viewAll', 'View all')} →
        </button>
      </div>
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 16, borderBottom: '1px solid var(--c-border-subtle)' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: teamAvg >= 4 ? '#22c55e' : teamAvg >= 3 ? '#f59e0b' : '#ef4444' }}>
            {teamAvg != null ? teamAvg.toFixed(2) : '—'}
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--c-text-muted)', marginLeft: 6 }}>/ 5</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>
            {totalRatings} {t('dashboard.widgets.ticketRatingsTotal', 'ratings · 30d')}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <ThumbsUp size={12} color="#22c55e" />
            <span style={{ fontWeight: 600 }}>{positives}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <ThumbsDown size={12} color="#ef4444" />
            <span style={{ fontWeight: 600 }}>{negatives}</span>
          </div>
        </div>
      </div>
      {recent.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 13 }}>
          {t('dashboard.widgets.ticketRatingsEmpty', 'No recent ratings')}
        </div>
      ) : (
        recent.map(r => {
          const ratingColor = r.rating >= 4 ? '#22c55e' : r.rating === 3 ? '#f59e0b' : '#ef4444';
          return (
            <div
              key={r.id}
              onClick={() => navigate(`/tickets/${r.ticket_id}`)}
              style={{
                padding: '10px 16px',
                borderBottom: '1px solid var(--c-border-subtle)',
                cursor: 'pointer',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--c-surface-2)')}
              onMouseLeave={e => (e.currentTarget.style.background = '')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: ratingColor }}>
                  {'★'.repeat(r.rating)}{'☆'.repeat(Math.max(0, 5 - r.rating))}
                </span>
                <span style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                  #{r.ticket_number} · {r.subject || ''}
                </span>
                <span style={{ fontSize: 10, color: 'var(--c-text-subtle)', flexShrink: 0 }}>
                  {formatDistanceToNow(new Date(r.created_at), { addSuffix: true, locale })}
                </span>
              </div>
              {r.comment && (
                <div style={{ fontSize: 11, color: 'var(--c-text-muted)', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  "{r.comment}"
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
